/*
 * Welcome signup — Klaviyo pop-up → HubSpot bridge.
 * See welcome-signup-spec.md.
 *
 * Klaviyo hosts and renders the welcome pop-up, and its own form completes the
 * Klaviyo subscription. This file is invisible: it has no markup, no CSS and no
 * visitor-facing behaviour. It listens for Klaviyo's form events and forwards
 * the submitted values to HubSpot, which is the pipe Attentive used to provide
 * from their side and which disappears when Attentive is switched off.
 *
 * There is therefore exactly ONE outbound call here — HubSpot — unlike
 * footer-email-signup.js, which has to call both vendors.
 *
 * Not a custom element, deliberately. Custom elements are this theme's
 * convention (24 definitions in scripts.js alone), but they upgrade a host
 * element and this integration has no DOM of its own. A guarded IIFE is the
 * honest shape (spec §6).
 *
 * ---------------------------------------------------------------------------
 * Event contract — verified against CCW's own form on 2026-09-10, not assumed
 * ---------------------------------------------------------------------------
 * Captured live from form YqPrDX at
 * www.crowdcontrolwarehouse.com/klaviyo-form-test:
 *
 *   stepSubmit  metaData { $email, $source: "Email and SMS Sign Up",
 *                          $step_name: "Email Opt-In" }
 *   submit      metaData { $email, $source }          <- fires AFTER stepSubmit
 *   viewedStep  metaData: null                        <- undocumented; null!
 *
 * Four consequences, each of which is a bug if ignored:
 *
 *  1. Keys are `$`-prefixed. `metaData.email` is undefined and looks exactly
 *     like "the visitor left it blank".
 *  2. `submit` is NOT a completion event — it fires on step 1, before the form
 *     advances to the phone step. `stepSubmit` is a superset and fires first,
 *     so we listen to `stepSubmit` and ignore `submit` entirely. Handling both
 *     double-processes every step.
 *  3. `viewedStep` arrives with `metaData: null`, so the handler must
 *     null-guard before touching it.
 *  4. No single event carries both email and phone. Step 1 has the email, step
 *     2 has the phone. Forwarding each event in isolation means the step-2
 *     payload has a phone number and no email — and since HubSpot keys the
 *     contact on email, that submission has no key and the phone is lost. Every
 *     phone number, on every signup. Hence the accumulator below.
 */

(function () {
  "use strict";

  if (window.__welcomeSignupInstalled) {
    return;
  }
  window.__welcomeSignupInstalled = true;

  var config = window.welcomeSignup;

  if (!config) {
    console.error(
      "[welcome-signup] window.welcomeSignup is missing. Is snippets/welcome-signup-config.liquid rendered before this script?",
    );
    return;
  }

  /*
   * Accumulated per form, not in one global: two Klaviyo forms open on a single
   * page would otherwise cross-contaminate each other's values.
   */
  var signupsByFormId = {};

  function recordSignupEvent(detail) {
    if (!detail || detail.type !== "stepSubmit") {
      return;
    }

    // viewedStep and close arrive with metaData null or absent.
    var metaData = detail.metaData;

    if (!metaData || !detail.formId) {
      return;
    }

    if (!signupsByFormId[detail.formId]) {
      signupsByFormId[detail.formId] = { formId: detail.formId };
    }

    var signup = signupsByFormId[detail.formId];

    if (metaData.$email) {
      signup.email = metaData.$email;
    }

    /*
     * $phone_number is INFERRED, not yet captured. The step-2 input on CCW's
     * form is id="phone_number_01M2…" name="phone-number" type="tel", and the
     * $-prefix convention is confirmed on three other keys, so this is a strong
     * prior — but the only submission captured was email-only, because testing
     * with an invented number sends a real confirmation SMS to a real stranger.
     * $phone is accepted as a fallback. If a phone number never reaches
     * HubSpot, log detail.metaData on a two-step submission and check the key
     * before changing anything else.
     */
    if (metaData.$phone_number || metaData.$phone) {
      signup.phone = metaData.$phone_number || metaData.$phone;
    }

    /*
     * The Klaviyo form's own name, e.g. "Email and SMS Sign Up". brimarstore
     * writes this same value, so reporting lines up across the properties with
     * one query rather than a per-site special case.
     */
    if (metaData.$source) {
      signup.source = metaData.$source;
    }

    /*
     * Nothing is sent until there is an email to key the contact on. Each step
     * then sends its own submission, and that is correct: the Forms API upserts
     * on email, so step 1 creates the contact and step 2 updates the same one.
     * Two requests, one contact.
     *
     * Do not try to defer until the form is "complete" — there is no reliable
     * completion signal, and a visitor who abandons after step 1 must still
     * have their email captured. That is the entire point of the ticket.
     */
    if (signup.email) {
      submitToHubSpot(signup);
    }
  }

  function submitToHubSpot(signup) {
    var portalId = config.hubspot.portalId;
    var formGuid = config.hubspot.formGuid;

    if (!portalId || !formGuid) {
      console.error(
        "[welcome-signup] HubSpot portalId or formGuid is empty in snippets/welcome-signup-config.liquid — no contact was created.",
      );
      return;
    }

    var fields = [{ objectTypeId: "0-1", name: "email", value: signup.email }];

    /*
     * `phone`, not `mobilephone`. Deliberate, carried from brimarstore: it is
     * the property CCW's business actually works from.
     *
     * KNOW WHAT THIS COSTS. This is an upsert, so a signup from someone who is
     * already a contact overwrites whatever number they already had — a
     * business number captured from an order, for example — with the mobile
     * number they typed into the pop-up. `mobilephone` would avoid that but
     * would put the number where nobody looks. The overwrite was accepted
     * knowingly. Do not "fix" this without raising it.
     */
    if (signup.phone) {
      fields.push({ objectTypeId: "0-1", name: "phone", value: signup.phone });
    }

    var sourceValue = signup.source || config.sourceFallback;

    if (config.hubspot.sourcePropertyName && sourceValue) {
      fields.push({
        objectTypeId: "0-1",
        name: config.hubspot.sourcePropertyName,
        value: sourceValue,
      });
    }

    /*
     * Deliberately NO `hutk`, unlike what the spec's §4 suggests carrying over
     * from the footer signup. HubSpot ranks the tracking cookie ABOVE the email
     * address when matching a submission to a contact:
     *
     *   different email + same hutk  ->  UPDATES THAT CONTACT'S EMAIL
     *
     * HubSpot's tracking script is live on CCW for portal 22740189 (injected
     * outside the theme, so it is not visible in theme source or the app-embed
     * list), which means every visitor carries a hubspotutk. Sending it made
     * three different addresses collapse onto one contact when this was tested
     * on the footer signup. It is worse here: this is a two-step flow that
     * depends on step 2 updating the same contact step 1 created, so a
     * mismatched cookie would land the phone number on a stranger's record.
     *
     * pageUri still carries the useful half of the attribution.
     */
    var body = {
      fields: fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    fetch(
      "https://api.hsforms.com/submissions/v3/integration/submit/" +
        encodeURIComponent(portalId) +
        "/" +
        encodeURIComponent(formGuid),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    )
      .then(function (response) {
        if (response.ok) {
          return null;
        }

        return response.text().then(function (detail) {
          /*
           * FIELD_NOT_IN_FORM_DEFINITION here means the HubSpot form does not
           * define one of email / phone / the source property. The whole
           * submission is rejected, not partially written — no contact, no
           * email, no phone.
           */
          console.error(
            "[welcome-signup] HubSpot returned " + response.status + ":",
            detail,
          );
        });
      })
      .catch(function (error) {
        /*
         * A failure here loses the HubSpot contact but NOT the subscriber:
         * Klaviyo's own form completed the subscription before this listener
         * ran, so the profile is safely in Klaviyo. That makes a bulk
         * Klaviyo -> HubSpot backfill a genuine recovery path for a systematic
         * outage, which is why there is no queue or retry here.
         */
        console.error("[welcome-signup] HubSpot contact not created:", error);
      });
  }

  /*
   * Take over from the synchronous buffer installed in the document head.
   *
   * That buffer exists because this script is deferred and therefore runs after
   * HTML parsing — potentially after Klaviyo has already dispatched an event.
   * This is not theoretical: during the live capture the form had already
   * rendered before a listener attached from the console could see it, and the
   * `open` event was missed entirely. On a fast page with an immediate-display
   * form, the first stepSubmit would be lost the same way.
   *
   * The head buffer's own klaviyoForms listener stays installed and remains the
   * single event source; all we do is swap where it pushes to, then replay
   * whatever accumulated. Adding a second addEventListener here would
   * double-process every event.
   */
  var buffered = Array.isArray(window._klSignupEvents)
    ? window._klSignupEvents
    : [];

  window._klSignupEvents = { push: recordSignupEvent };

  buffered.forEach(recordSignupEvent);
})();
